/**
 * LedgerCard — shows aggregated customer ledger totals + actions.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, fmtPKR } from "../lib/api";
import { Wallet, TrendingUp, Receipt, Download, MessageCircle, FileText } from "lucide-react";
import { buildPublicLedgerUrl } from "../lib/publicUrl";
import NativeShareButton from "./NativeShareButton";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function Tile({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber: "bg-amber-50 border-amber-200",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      </div>
      <div className="text-lg font-display font-bold">{value}</div>
    </div>
  );
}

export default function LedgerCard({ customerId, customer }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!customerId) return;
    api.get(`/customers/${customerId}/ledger`).then((r) => setData(r.data)).catch(() => {});
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const t = data.totals;
  const pdfUrl = `${API_BASE}/api/customers/${customerId}/ledger.pdf?shop=${data.shop.id}`;
  // Customer-facing share URL uses HisaabPro domain
  const publicPdfUrl = buildPublicLedgerUrl(customerId, data.shop.id);
  const waMsg = `Assalam o Alaikum ${data.customer.name},\n\nAap ki ledger summary:\n• Total Loan: PKR ${t.total_loan_amount.toLocaleString()}\n• Paid: PKR ${t.total_paid.toLocaleString()}\n• Remaining: PKR ${t.remaining.toLocaleString()}\n\nDetailed PDF:\n${publicPdfUrl}\n\n— ${data.shop.name}`;
  const waLink = customer?.mobile
    ? `https://wa.me/${(customer.mobile || "").replace(/\D/g, "").replace(/^0/, "92")}?text=${encodeURIComponent(waMsg)}`
    : null;

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="ledger-card">
      <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-emerald-600" />
          <div className="font-display font-semibold">Customer Ledger</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={pdfUrl} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-md text-sm font-semibold"
             data-testid="ledger-pdf-btn">
            <Download className="w-3.5 h-3.5" /> PDF
          </a>
          <NativeShareButton
            title="Customer Ledger"
            text={`Ledger for ${customer?.name || "customer"}`}
            url={pdfUrl}
            label="Share"
            variant="outline"
            testid="ledger-share-btn"
          />
          {waLink && (
            <a href={waLink} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-sm font-semibold"
               data-testid="ledger-wa-btn">
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </a>
          )}
        </div>
      </div>
      <div className="p-5 grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile icon={Wallet} label="Total Loan" value={fmtPKR(t.total_loan_amount)} testid="led-total-loan" />
        <Tile icon={TrendingUp} label="Total Paid" value={fmtPKR(t.total_paid)} tone="emerald" testid="led-paid" />
        <Tile icon={Receipt} label="Remaining" value={fmtPKR(t.remaining)} tone="amber" testid="led-remaining" />
        <Tile icon={Receipt} label="Active Loans" value={t.active_loans} testid="led-active" />
        <Tile icon={Receipt} label="Closed Loans" value={t.closed_loans} testid="led-closed" />
      </div>
      {/* Payment history (recent 10) */}
      {data.payments?.length > 0 && (
        <div className="border-t border-slate-200 px-5 py-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Recovery History (recent)</div>
          <div className="space-y-1 max-h-44 overflow-y-auto text-sm">
            {data.payments.slice(0, 10).map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 pb-1">
                <span className="text-slate-600 tabular text-xs">{p.date} · {p.method || "—"}</span>
                <span className="font-semibold tabular">{fmtPKR(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
